import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { auth, db } from '../../config/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, getDoc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import '../../styles/Chat.css';

const Chat = ({ isEmbedded = false }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [relationshipStatus, setRelationshipStatus] = useState({
        connected: false,
        partnerName: null,
        partnerId: null,
        loading: true
    });
    const [replyTo, setReplyTo] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const messagesEndRef = useRef(null);
    const messageContainerRef = useRef(null);
    const toast = useRef(null);
    const currentUser = auth.currentUser;
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);

    // Helper function to get chat ID from user IDs
    const getChatId = (userId1, userId2) => {
        const sortedIds = [userId1, userId2].sort();
        return `${sortedIds[0]}_${sortedIds[1]}`;
    };

    // Helper function to check if message is older than 24 hours
    const isMessageExpired = (timestamp) => {
        if (!timestamp || !timestamp.toDate) return false;

        const messageDate = timestamp.toDate();
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);

        return messageDate < oneDayAgo;
    };

    // Function to delete expired messages
    const deleteExpiredMessages = (messages) => {
        messages.forEach(message => {
            if (isMessageExpired(message.timestamp)) {
                if (isEmbedded) {
                    const chatId = getChatId(currentUser.uid, relationshipStatus.partnerId);
                    deleteDoc(doc(db, "chats", chatId, "messages", message.id))
                        .catch(error => console.error("Error deleting expired message:", error));
                } else {
                    deleteDoc(doc(db, "chats", message.id))
                        .catch(error => console.error("Error deleting expired message:", error));
                }
            }
        });
    };

    useEffect(() => {
        const fetchConnectionStatus = async () => {
            if (!currentUser) {
                setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                return;
            }

            try {
                // Get user information from Firestore to check relationship status
                const userRef = doc(db, 'UserInformation', currentUser.uid);
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) {
                    setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                    return;
                }

                const userData = userSnap.data();

                if (!userData.inRelation || !userData.inRelationWith) {
                    setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                    return;
                }

                // Get partner information
                const partnerRef = doc(db, 'UserInformation', userData.inRelationWith);
                const partnerSnap = await getDoc(partnerRef);

                if (!partnerSnap.exists()) {
                    setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                    return;
                }

                const partnerData = partnerSnap.data();

                // Verify both users are connected to each other
                if (partnerData.inRelation && partnerData.inRelationWith === currentUser.uid) {
                    setRelationshipStatus({
                        connected: true,
                        partnerName: partnerData.displayName || partnerData.email || 'Partner',
                        partnerId: userData.inRelationWith,
                        loading: false
                    });
                } else {
                    setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                }
            } catch (error) {
                console.error("Error fetching connection status:", error);
                setRelationshipStatus({ connected: false, partnerName: null, partnerId: null, loading: false });
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch connection status'
                });
            }
        };

        fetchConnectionStatus();
        const intervalId = setInterval(fetchConnectionStatus, 30000);
        return () => clearInterval(intervalId);
    }, [currentUser]);

    // Monitor scroll position
    useEffect(() => {
        const handleScroll = () => {
            if (!messageContainerRef.current) return;

            const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShouldScrollToBottom(isNearBottom);
        };

        const container = messageContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Use Firestore snapshot to listen for messages and handle message expiration
    useEffect(() => {
        if (!currentUser || !relationshipStatus.partnerId || !relationshipStatus.connected) return;

        let chatQuery;

        if (isEmbedded) {
            const chatId = getChatId(currentUser.uid, relationshipStatus.partnerId);
            chatQuery = query(
                collection(db, "chats", chatId, "messages"),
                orderBy("timestamp", "asc")
            );
        } else {
            chatQuery = query(
                collection(db, "chats"),
                where("participants", "array-contains", currentUser.uid),
                orderBy("timestamp", "asc")
            );
        }

        const unsubscribe = onSnapshot(chatQuery, (snapshot) => {
            const messageList = [];

            snapshot.forEach((doc) => {
                const data = doc.data();

                if (isEmbedded ||
                    ((data.senderId === currentUser.uid && data.receiverId === relationshipStatus.partnerId) ||
                        (data.senderId === relationshipStatus.partnerId && data.receiverId === currentUser.uid))) {
                    messageList.push({
                        id: doc.id,
                        ...data,
                        replyTo: data.replyTo || null
                    });
                }
            });

            // Check for and delete expired messages
            deleteExpiredMessages(messageList);

            // Filter out expired messages from display
            const validMessages = messageList.filter(msg => !isMessageExpired(msg.timestamp));
            setMessages(validMessages);
        }, (error) => {
            console.error("Error fetching messages: ", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load messages'
            });
        });

        return () => unsubscribe();
    }, [currentUser, relationshipStatus.partnerId, relationshipStatus.connected, isEmbedded]);

    // Scroll to bottom when needed
    useEffect(() => {
        if (shouldScrollToBottom && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, shouldScrollToBottom]);

    // Handle clicking outside dropdown
    useEffect(() => {
        const handleClickOutside = () => setActiveDropdown(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const toggleDropdown = (messageId, event) => {
        if (event) {
            event.stopPropagation();
        }
        setActiveDropdown(activeDropdown === messageId ? null : messageId);
    };

    const deleteMessage = async (messageId) => {
        try {
            if (isEmbedded) {
                const chatId = getChatId(currentUser.uid, relationshipStatus.partnerId);
                await deleteDoc(doc(db, "chats", chatId, "messages", messageId));
            } else {
                await deleteDoc(doc(db, "chats", messageId));
            }

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Message deleted'
            });
        } catch (error) {
            console.error("Error deleting message:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete message'
            });
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !currentUser || !relationshipStatus.partnerId) return;

        try {
            const messageData = {
                content: newMessage,
                senderId: currentUser.uid,
                receiverId: relationshipStatus.partnerId,
                timestamp: serverTimestamp(),
                read: false
            };

            if (replyTo) {
                messageData.replyTo = {
                    id: replyTo.id,
                    content: replyTo.content
                };
            }

            if (isEmbedded) {
                const chatId = getChatId(currentUser.uid, relationshipStatus.partnerId);
                const chatRef = doc(db, "chats", chatId);
                const chatSnap = await getDoc(chatRef);

                if (!chatSnap.exists()) {
                    await setDoc(chatRef, {
                        participants: [currentUser.uid, relationshipStatus.partnerId],
                        createdAt: serverTimestamp()
                    });
                }

                await addDoc(collection(db, "chats", chatId, "messages"), messageData);
            } else {
                messageData.participants = [currentUser.uid, relationshipStatus.partnerId];
                await addDoc(collection(db, "chats"), messageData);
            }

            setNewMessage('');
            setShowEmojiPicker(false);
            setReplyTo(null);
            setShouldScrollToBottom(true);
        } catch (error) {
            console.error("Error sending message:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send message'
            });
        }
    };

    const onEmojiClick = (emoji) => {
        setNewMessage(prevMessage => prevMessage + emoji);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            sendMessage();
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error("Error formatting timestamp:", error);
            return '';
        }
    };

    const renderMessage = (message, index) => {
        const isCurrentUser = message.senderId === currentUser?.uid;
        const isDropdownOpen = activeDropdown === message.id;

        return (
            <div
                key={message.id || index}
                className={`message-item ${isCurrentUser ? 'sent' : 'received'}`}
            >
                <div className="message-content">
                    {message.replyTo && (
                        <div className="reply-to">
                            <div className="reply-text">{message.replyTo.content}</div>
                        </div>
                    )}
                    <div className="message-text">{message.content}</div>
                    <div className="message-time-actions">
                        <span className="message-time">{formatTime(message.timestamp)}</span>
                        <i
                            className="pi pi-chevron-down message-menu-toggle"
                            onClick={(e) => toggleDropdown(message.id, e)}
                        />
                    </div>

                    {isDropdownOpen && (
                        <div className="message-dropdown">
                            <div
                                className="dropdown-item"
                                onClick={() => {
                                    setReplyTo(message);
                                    setActiveDropdown(null);
                                }}
                            >
                                <i className="pi pi-reply"></i> Reply
                            </div>

                            {isCurrentUser && (
                                <div
                                    className="dropdown-item delete"
                                    onClick={() => {
                                        deleteMessage(message.id);
                                        setActiveDropdown(null);
                                    }}
                                >
                                    <i className="pi pi-trash"></i> Delete
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!relationshipStatus.connected && !relationshipStatus.loading) {
        return (
            <div className={`chat-container ${isEmbedded ? 'embedded' : ''}`}>
                <Toast ref={toast} />
                <Card title={isEmbedded ? null : "Chat"} className="chat-card">
                    <div className="not-connected-message">
                        <p>You need to be connected with a partner to use the chat feature.</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (relationshipStatus.loading) {
        return (
            <div className={`chat-container ${isEmbedded ? 'embedded' : ''}`}>
                <Toast ref={toast} />
                <Card title={isEmbedded ? null : "Chat"} className="chat-card">
                    <div className="loading-message">
                        <p>Loading chat...</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className={`chat-container ${isEmbedded ? 'embedded' : ''}`}>
            <Toast ref={toast} />
            <Card title={isEmbedded ? null : `Chat with ${relationshipStatus.partnerName}`} className="chat-card">
                <div className="messages-container" ref={messageContainerRef}>
                    {messages.length === 0 ? (
                        <div className="no-messages">
                            <p>No messages yet. Start the conversation!</p>
                        </div>
                    ) : (
                        messages.map((message, index) => renderMessage(message, index))
                    )}
                    <div ref={messagesEndRef} />
                </div>
                <div className="message-input-container">
                    {replyTo && (
                        <div className="reply-preview">
                            <div className="reply-content">
                                <span className="reply-label">Replying to:</span>
                                <span className="reply-text">{replyTo.content}</span>
                            </div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-rounded p-button-text p-button-sm"
                                onClick={() => setReplyTo(null)}
                            />
                        </div>
                    )}
                    <InputText
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        className="message-input"
                    />
                    <Button
                        icon="pi pi-star"
                        className="p-button-rounded p-button-text emoji-button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    />
                    <Button
                        icon="pi pi-send"
                        className="p-button-rounded send-button"
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                    />
                </div>
                {showEmojiPicker && (
                    <div className="emoji-picker-container">
                        <div className="emoji-grid">
                            {['😀', '😂', '😍', '🥰', '😊', '😎', '🙄', '😢', '❤️', '👍', '👎', '👋', '🎉', '🔥', '💯', '🤔'].map(emoji => (
                                <div
                                    key={emoji}
                                    className="emoji-item"
                                    onClick={() => onEmojiClick(emoji)}
                                >
                                    {emoji}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Chat;
